import { ChangeDetectorRef, Component, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { filter, map, takeUntil } from 'rxjs/operators';

import { WithCompareByIdFn } from 'core/mixins/with-compare-by-id-fn';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { getOppositeEntityTypeFromConnection } from 'entity/helpers/get-connected-to-entity-type';
import { isNilOrEmpty } from 'helpers';

import {
  EntityConnectionTypeDetailsFragment,
  EntityPublicInfoFragment,
  GetEntityConnectionTypesGQL
} from 'generated/graphql';

export interface ConnectionFormValue {
  selectedEntity: EntityPublicInfoFragment;
  entityConnectionType: EntityConnectionTypeDetailsFragment;
  note: string;
}

@Component({
  selector: 'app-create-connection-form',
  templateUrl: './create-connection-form.component.html',
  styleUrls: ['./create-connection-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CreateConnectionFormComponent),
      multi: true
    }
  ]
})
export class CreateConnectionFormComponent extends WithDisposableSubject(
  WithCompareByIdFn(class { })
) implements OnInit, ControlValueAccessor {

  @Input() campaignId: string;
  @Input() fromEntityTypeId: string;
  @Input() entityConnectionType: EntityConnectionTypeDetailsFragment;

  entityConnectionOptions: EntityConnectionTypeDetailsFragment[];
  toEntityType: { nameSingular: string; id: string };
  selectedEntity: EntityPublicInfoFragment;

  entityConnectionTypeInput = new UntypedFormControl();
  noteInput = new UntypedFormControl();

  // ControlValueAccessor properties
  private _value: ConnectionFormValue;
  private _onChange = (value: ConnectionFormValue) => {};
  private _onTouched = () => {};
  disabled = false;

  constructor(
    private _getEntityConnectionTypes: GetEntityConnectionTypesGQL,
    private _cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this._setupForm();
  }

  // ControlValueAccessor implementation
  writeValue(value: ConnectionFormValue): void {
    this._value = value;
    if (value) {
      this.selectedEntity = value.selectedEntity;
      this.entityConnectionTypeInput.setValue(value.entityConnectionType, { emitEvent: false });
      this.noteInput.setValue(value.note, { emitEvent: false });

      if (value.entityConnectionType) {
        this.toEntityType = getOppositeEntityTypeFromConnection(
          this.fromEntityTypeId, value.entityConnectionType
        );
      }
    }
    this._cd.markForCheck();
  }

  registerOnChange(fn: (value: ConnectionFormValue) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.entityConnectionTypeInput.disable();
      this.noteInput.disable();
    } else {
      this.entityConnectionTypeInput.enable();
      this.noteInput.enable();
    }
  }

  entitySelected(event: EntityPublicInfoFragment) {
    this.selectedEntity = event;
    this._onTouched();
    this._updateValue();
  }

  excludeEntitiesFn(entityId: string) {
    return function(results: EntityPublicInfoFragment[]) {
      return results.filter(e => e.id !== entityId);
    };
  }

  getOppositeEntityType(ect: EntityConnectionTypeDetailsFragment) {
    return getOppositeEntityTypeFromConnection(this.fromEntityTypeId, ect);
  }

  private _setupForm() {
    // if an entityConnectionType is provided, set the selected form value
    // and the toEntityType
    if (this.entityConnectionType) {
      this.toEntityType = getOppositeEntityTypeFromConnection(
        this.fromEntityTypeId, this.entityConnectionType
      );
      this.entityConnectionTypeInput.setValue(this.entityConnectionType);
    }

    // Watch for changes on connection Type input
    this.entityConnectionTypeInput.valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe(value => {
        // Reset selected entity if changed entityType
        this.selectedEntity = null;
        // Set to entity type (avoids multiple fn calls on change detection)
        this.toEntityType = getOppositeEntityTypeFromConnection(
          this.fromEntityTypeId, value
        );
        this._cd.markForCheck();
        this._onTouched();
        this._updateValue();
      });

    // Watch for note changes
    this.noteInput.valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe(() => {
        this._onTouched();
        this._updateValue();
      });

    this._getEntityConnectionTypes.watch({
      campaignId: this.campaignId,
      entityTypeId: this.fromEntityTypeId
    }, {
      fetchPolicy: 'cache-and-network'
    }).valueChanges
      .pipe(
        takeUntil(this.disposer),
        filter(res => !!res.data),
        map(({ data }) =>
          // Filter to only connection types where a valid entity type id is the current entity type
          data.getEntityConnectionTypes
            .filter(ect => [ect.entityType1.id, ect.entityType2.id].includes(this.fromEntityTypeId))
        )
      ).subscribe(
        (ects) => {
          this.entityConnectionOptions = ects;
          // Set to the first type if there is only one type
          if (isNilOrEmpty(this.entityConnectionTypeInput.value)) {
            if (ects.length === 1) {
              this.entityConnectionTypeInput.setValue(ects[0]);
            }
          }
          this._cd.markForCheck();
        }
      );
  }

  private _updateValue() {
    const newValue: ConnectionFormValue = {
      selectedEntity: this.selectedEntity,
      entityConnectionType: this.entityConnectionTypeInput.value,
      note: this.noteInput.value
    };

    this._value = newValue;
    this._onChange(newValue);
  }
}
